import {ActionTree, GetterTree, Module, MutationTree} from 'vuex'
import {AuthState, RootState} from '@/types/store'
import {$} from 'simpli-web-sdk'
import {Role} from '@/enums/Role'
import AuthRequest from '@/model/request/AuthRequest'
import AuthResponse from '@/model/response/AuthResponse'
import ResetPasswordRequest from '@/model/request/ResetPasswordRequest'
import RecoverPasswordRequest from '@/model/request/RecoverPasswordRequest'
import Responsavel from '@/model/resource/Responsavel'
import * as Helper from '@/helpers'

// initial state
const state: AuthState = {
  token: null,
  role: null,
  queryToken: null,
  responsavel: null,
  members: [],
  cachePath: null,
  regraExibicaoGrupoEconomico: null,
  eventListener: {
    signIn: [],
    auth: [],
    signOut: [],
  },
}

// getters
const getters: GetterTree<AuthState, RootState> = {
  isLogged: ({token, queryToken}) => Boolean(token || queryToken),
  token: ({token}) => token,
  role: ({role}) => role,
  queryToken: ({queryToken}) => queryToken,
  responsavel: ({responsavel}) => responsavel || new Responsavel(),
  members: ({members}) => members,
  firstMemberOrNull: ({members}) => members.length === 1 ? members[0] : null,
  cachePath: ({cachePath}) => cachePath,
  regraExibicaoGrupoEconomico: ({regraExibicaoGrupoEconomico}) => regraExibicaoGrupoEconomico,
}

// actions
const actions: ActionTree<AuthState, RootState> = {
  /**
   * Sign in account
   * @param state
   * @param commit
   * @param getters
   * @param request
   * @param welcome
   */
  signIn: async ({state, commit, getters}, request: AuthRequest) => {
    const authResponse = new AuthResponse()

    await authResponse.signIn(request)
    if (authResponse.token) localStorage.setItem('token', authResponse.token)

    commit('POPULATE_TOKEN')

    let path = '/cockpit'
    if (authResponse.role === Role.SAC) {
      path = '/reports/delivery/sac'
    }

    if (authResponse.responsavel) {
      $.snotify.info(authResponse.responsavel.$tag, $.t('system.info.welcome'))
    }

    Helper.push(getters.cachePath || path)

    commit('SET_CACHE_PATH', null)

    state.eventListener.signIn.forEach((item) => item(authResponse))
  },

  /**
   * Verifies authorization and refresh user info.
   * Note: If it is not logged then dispatches signOut
   * @param dispatch
   * @param commit
   * @param getters
   * @param spinner
   */
  auth: async ({dispatch, commit, getters}, spinner = 'auth') => {
    if (getters.queryToken) return

    commit('POPULATE_TOKEN')

    if (!getters.isLogged) {
      commit('SET_CACHE_PATH', $.route.path)

      Helper.error('system.error.unauthorized')
      dispatch('signOut')
      return
    }

    const authResponse = new AuthResponse()

    await authResponse.authenticate(spinner)

    commit('POPULATE', authResponse)

    Helper.setIdFilial(getters.firstMemberOrNull && getters.firstMemberOrNull.$id || 0)
    Helper.setAllowNotification(getters.responsavel && getters.responsavel.receberNotificacoesCarga || false)
    Helper.setRole(authResponse.role && authResponse.role.toString() || '')

    state.eventListener.auth.forEach((item) => item(authResponse))
  },

  /**
   * Sign out account
   * @param state
   * @param commit
   * @param showError
   */
  signOut: ({state, commit}) => {
    Helper.push('/sign-in')

    commit('FORGET')
    state.eventListener.signOut.forEach((item) => item())
    localStorage.clear()
  },

  /**
   * Reset password
   * @param context
   * @param request
   */
  resetPassword: async (context, request: ResetPasswordRequest) => {
    await new AuthResponse().resetPassword(request)
    Helper.successAndPush('system.success.resetPassword', '/sign-in')
  },

  /**
   * Recover password
   * @param context
   * @param request
   */
  recoverPassword: async (context, request: RecoverPasswordRequest) => {
    await new AuthResponse().recoverPassword(request)
    Helper.successAndPush('system.success.recoverPassword', '/sign-in')
  },

  /**
   * Change password
   * @param context
   * @param request
   */
  changePassword: async ({dispatch, getters, commit}, request: Responsavel) => {
    const token = await new AuthResponse().changePassword(request)
    if (token) localStorage.setItem('token', token)
    commit('POPULATE_TOKEN')

    Helper.success('system.success.recoverPassword')
  },

  /**
   * On SignIn Event
   * @param dispatch
   * @param callback
   */
  onSignIn: ({dispatch}, callback) => dispatch('addEventListener', {name: 'signIn', callback}),

  /**
   * On Auth Event
   * @param dispatch
   * @param callback
   */
  onAuth: ({dispatch}, callback) => dispatch('addEventListener', {name: 'auth', callback}),

  /**
   * On SignOut Event
   * @param dispatch
   * @param callback
   */
  onSignOut: ({dispatch}, callback) => dispatch('addEventListener', {name: 'signOut', callback}),

  /**
   * Add event listener
   * @param commit
   * @param payload {name, callback}
   */
  addEventListener: ({commit}, payload) => commit('ADD_EVENT_LISTENER', payload),

  /**
   * Remove event listener
   * @param commit
   * @param payload
   */
  removeEventListener: ({commit}, payload) => commit('REMOVE_EVENT_LISTENER', payload),
}

// mutations
const mutations: MutationTree<AuthState> = {
  // Populate token mutation
  POPULATE_TOKEN(state) {
    state.token = localStorage.getItem('token') || null
  },

  // Populate query token mutation
  SET_QUERY_TOKEN(state, val) {
    state.queryToken = val || null
  },

  // Populate user and plan mutation
  POPULATE(state, response: AuthResponse) {
    state.role = response.role
    state.responsavel = response.responsavel
    state.members = response.members
    state.regraExibicaoGrupoEconomico = response.regraExibicaoGrupoEconomico
  },

  // Forget mutation
  FORGET(state) {
    state.token = null
    state.role = null
    state.responsavel = null
    state.members = []
    state.regraExibicaoGrupoEconomico = null

    localStorage.removeItem('token')
  },

  // Set Cache Path mutation
  SET_CACHE_PATH(state, val) {
    const blacklist = [
      /\/sign-in/,
      /\/password\/reset/,
      /\/password\/recover\/\S+/,
      /\/delivery\/get\/\d+\/\S+/,
    ]

    for (const pattern of blacklist) {
      if (pattern.exec(val)) {
        state.cachePath = null
        return
      }
    }

    state.cachePath = val
  },

  // Add Event Listener mutation
  ADD_EVENT_LISTENER(state, {name, callback}) {
    state.eventListener[name].push(callback)
  },

  // Remove Event Listener mutation
  REMOVE_EVENT_LISTENER(state, {name, callback}) {
    if (callback) {
      const index = state.eventListener[name].findIndex((item) => item === callback)
      state.eventListener[name].splice(index, 1)
    } else {
      state.eventListener[name] = []
    }
  },
}

const namespaced: boolean = true
export const auth: Module<AuthState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
}
